import { useState, useEffect } from "react";
import DelhiveryTransitModal from "@/components/DelhiveryTransitModal";
import { Package, Search, Download, MapPin, RefreshCw, Loader2, Settings } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import HandoffModeToggle from "@/components/shared/HandoffModeToggle";
import ManifestReviewModal from "@/components/ManifestReviewModal";
import PickupRaiseDialog from "@/components/PickupRaiseDialog";
import PickupCancelDialog from "@/components/PickupCancelDialog";
import NddWatchBanner from "@/components/NddWatchBanner";
import PaymentSwitchDialog from "@/components/PaymentSwitchDialog";
import ShipmentEditModal, { canEditShipment } from "@/components/ShipmentEditModal";
import FinaliseBillDialog from "@/components/FinaliseBillDialog";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/utils/api";
import { toast } from "sonner";

const STATUS_OPTIONS = ["all", "booked", "picked_up", "in_transit", "out_for_delivery", "delivered", "cancelled"];

export default function AllShipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedForCombine, setSelectedForCombine] = useState(new Set());
  const [combining, setCombining] = useState(false);
  const [combineResult, setCombineResult] = useState(null);
  const [cancelModal, setCancelModal] = useState(null); // shipment being cancelled, or null
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelPnpr, setCancelPnpr] = useState(true);

  const handleCancelShipment = async () => {
    if (!cancelModal || !cancelReason.trim()) return;
    setCancelling(true);
    try {
      await api.post(`/shipments/${cancelModal.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Shipment cancelled");
      if (cancelPnpr && cancelModal.assigned_carrier === "DELHIVERY_B2B" && cancelModal.delhivery_lrnum) await downloadPnpr(cancelModal, cancelReason.trim());
      setCancelModal(null);
      setCancelReason("");
      fetchShipments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to cancel shipment");
    } finally {
      setCancelling(false);
    }
  };

  const selectedShipments = shipments.filter((s) => selectedForCombine.has(s.id));
  const selectedCustomerIds = new Set(selectedShipments.map((s) => s.customer_id));
  const sameCustomer = selectedCustomerIds.size <= 1;

  const handleCombineClick = async () => {
    if (selectedShipments.length < 2 || !sameCustomer) return;
    setCombining(true);
    try {
      const res = await api.post("/v2/invoices/combine", {
        shipment_ids: selectedShipments.map((s) => s.id),
        customer_id: selectedShipments[0].customer_id,
      });
      setCombineResult(res.data);
      setSelectedForCombine(new Set());
      fetchShipments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to combine invoices");
    } finally {
      setCombining(false);
    }
  };

  async function downloadCombinedFile(url, filename) {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(dlUrl);
    } catch {
      toast.error("Download failed");
    }
  }
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [networkFilter, setNetworkFilter] = useState("all"); // all | own | 3pl
  const [nddLate, setNddLate] = useState(false); // only NDD shipments past the day after booking
  const [search, setSearch] = useState("");
  const [syncingId, setSyncingId] = useState(null);

  const downloadLabel = async (s) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/labels/${s.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Label download failed"); }
  };

  const sync3PL = async (s) => {
    setSyncingId(s.id);
    try {
      const lr = s.order_id || s.lr_number || s.tracking_id;
      const r = await api.post(`/v2/tracking/sync-3pl/${lr}`);
      toast.success(`Synced ${r.data.synced_count} events`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const is3pl = (s) => ["DELHIVERY_B2B", "DELHIVERY_B2C", "DELHIVERY_C2C"].includes((s.assigned_carrier || s.network_type || "").toUpperCase());
  const [manifestingId, setManifestingId] = useState(null);
  const [transitAwb, setTransitAwb] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [qrLoading, setQrLoading] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(null);

  const raiseInvoice = async (s) => {
    setInvoiceLoading(s.id);
    try {
      await api.post(`/shipments/${s.id}/invoice/generate`);
      toast.success("Invoice generated");
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate invoice");
    }
    setInvoiceLoading(null);
  };

  const viewEway = async (s, download = false) => {
    try {
      const res = await api.get(`/v2/shipment-docs/${s.id}/eway-bill/link`, { params: { download } });
      if (download) { const el = document.createElement("a"); el.href = res.data.url; el.download = res.data.filename || "eway-bill.pdf"; document.body.appendChild(el); el.click(); el.remove(); }
      else window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the e-way bill"); }
  };

  const viewInvoice = async (s) => {
    setInvoiceLoading(s.id);
    try {
      const res = await api.get(`/shipments/${s.id}/invoice/download-url`);
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open invoice");
    }
    setInvoiceLoading(null);
  };

  // ── Delivery Challan (B2B goods-in-transit document, separate from the
  // Tax Invoice above -- see utils/dc_generator.py, Sep 23 2026) ──
  const [dcLoading, setDcLoading] = useState(null);
  const [dcUploadModal, setDcUploadModal] = useState(null); // shipment being uploaded for
  const [dcNumber, setDcNumber] = useState("");
  const [dcValue, setDcValue] = useState("");
  const [dcFile, setDcFile] = useState(null);
  const [dcSubmitting, setDcSubmitting] = useState(false);

  const generateDC = async (s) => {
    setDcLoading(s.id);
    try {
      await api.post(`/shipments/${s.id}/delivery-challan/generate`);
      toast.success("Delivery Challan generated");
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate Delivery Challan");
    }
    setDcLoading(null);
  };

  const viewDC = async (s) => {
    setDcLoading(s.id);
    try {
      const res = await api.get(`/shipments/${s.id}/delivery-challan/download-url`);
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open Delivery Challan");
    }
    setDcLoading(null);
  };

  const submitDCUpload = async () => {
    if (!dcNumber.trim() || !dcValue || !dcFile) {
      toast.error("DC number, declared value and file are all required");
      return;
    }
    setDcSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("dc_number", dcNumber.trim());
      fd.append("declared_value", dcValue);
      fd.append("file", dcFile);
      await api.post(`/shipments/${dcUploadModal.id}/delivery-challan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Delivery Challan uploaded");
      setDcUploadModal(null);
      setDcNumber(""); setDcValue(""); setDcFile(null);
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setDcSubmitting(false);
    }
  };

  const [editAmountShipment, setEditAmountShipment] = useState(null);
  const [editAmountValue, setEditAmountValue] = useState("");
  const [editAmountRemark, setEditAmountRemark] = useState("");
  const [editAmountSubmitting, setEditAmountSubmitting] = useState(false);
  const [actionsShipment, setActionsShipment] = useState(null);
  const [finaliseShipment, setFinaliseShipment] = useState(null);
  const { user: authUser } = useAuth();
  const isAdminUser = authUser?.role === "admin";
  // Inline Assign feature (Sep 1 2026, simplified per Aditya's real
  // feedback: staff already picks the carrier at booking time via
  // Connection Through -- re-running the SOP recommend/override-reason
  // flow here was redundant friction for the common case). Static list
  // matching Connection Through's real 5 options exactly (same values
  // BookingConsole.jsx's dropdown uses, EXCEPT Delhivery C2C is
  // deliberately mapped to the real "DELHIVERY_B2C" carrier value, not
  // the dropdown's own "DELHIVERY_C2C" -- confirmed with Aditya, since
  // DELHIVERY_B2C is what all the real manifest/tracking logic actually
  // checks for everywhere else in the codebase; using C2C here would
  // have silently broken manifest triggering for anything assigned this
  // way). "Others" opens a free-text field. Still submits to the same
  // real POST /v2/assignments/{id}/assign endpoint -- zero backend changes.
  const [assignModal, setAssignModal] = useState(null); // shipment being assigned
  const [assignCarrier, setAssignCarrier] = useState(""); // selected carrier value
  const [assignOthersText, setAssignOthersText] = useState("");
  const [assignCarrierAwb, setAssignCarrierAwb] = useState("");
  const [assignHandoffMode, setAssignHandoffMode] = useState("FM_PICKUP");
  const [assigning, setAssigning] = useState(false);
  const [assignReassignReason, setAssignReassignReason] = useState("");
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);

  const ASSIGN_CARRIER_OPTIONS = [
    { value: "OWN_NETWORK", label: "Sendtoo Network" },
    { value: "DELHIVERY_B2B", label: "Delhivery B2B" },
    { value: "DELHIVERY_B2C", label: "Delhivery C2C" },
    { value: "INDIA_POST", label: "India Post" },
    { value: "OTHERS", label: "Others" },
  ];

  // Real bug fix (Sep 1 2026): BookingConsole's Connection Through
  // dropdown stores DIFFERENT raw values than our assign-carrier values
  // ("SENDTOO_NETWORK" not "OWN_NETWORK"; "DELHIVERY_C2C" not
  // "DELHIVERY_B2C", even though we deliberately assign using B2C) -- a
  // direct equality check silently never matched, so the green
  // highlight/pre-select/reorder never worked. This normalizes a real
  // shipment's connection_through value to our option values first.
  const normalizeConnectionThrough = (raw) => {
    const map = {
      SENDTOO_NETWORK: "OWN_NETWORK",
      DELHIVERY_C2C: "DELHIVERY_B2C",
      DELHIVERY_B2B: "DELHIVERY_B2B",
      INDIA_POST: "INDIA_POST",
      OTHERS: "OTHERS",
    };
    return map[raw] || "";
  };

  const openAssignModal = (s) => {
    setAssignModal(s);
    // Pre-select whatever was chosen at booking time (Connection Through)
    // -- real fix (Sep 1 2026) per Aditya's feedback, staff shouldn't have
    // to re-pick something they already decided at booking.
    const bookedValue = normalizeConnectionThrough(s.connection_through);
    setAssignCarrier(bookedValue);
    setAssignOthersText("");
    setAssignCarrierAwb("");
    setAssignHandoffMode("FM_PICKUP");
    setNearestCenter(null);
    setAssignReassignReason("");
    setShowReassignConfirm(false);
  };

  // Real reassignment guard (Sep 1 2026): if staff pick a DIFFERENT carrier
  // than what was chosen at booking time, require a mandatory remark before
  // proceeding -- acts as a real audit trail for why the network changed.
  const isReassigning = assignModal?.connection_through
    && normalizeConnectionThrough(assignModal.connection_through)
    && assignCarrier
    && assignCarrier !== normalizeConnectionThrough(assignModal.connection_through);

  const submitAssign = async () => {
    if (!assignCarrier) return toast.error("Select a carrier");
    if (assignCarrier === "OTHERS" && !assignOthersText.trim()) return toast.error("Enter the carrier name");
    if (isReassigning && !showReassignConfirm) { setShowReassignConfirm(true); return; }
    if (isReassigning && !assignReassignReason.trim()) return toast.error("A remark is required to change the network from what was selected at booking");
    const isOwn = assignCarrier === "OWN_NETWORK";
    const isDelhivery = assignCarrier.startsWith("DELHIVERY");
    if (!isOwn && !isDelhivery && !assignCarrierAwb.trim()) return toast.error("Enter carrier AWB number");
    if ((assignModal?.payment_mode === "ppp_cash" || assignModal?.payment_mode === "ppp_upi") && assignModal?.payment_status !== "paid") {
      if (!window.confirm("Payment not received — connect anyway?")) return;
    }
    setAssigning(true);
    try {
      const payload = {
        carrier: assignCarrier === "OTHERS" ? assignOthersText.trim() : assignCarrier,
        network_type: isOwn ? "OWN" : "3PL",
        carrier_awb: (isOwn || isDelhivery) ? "" : assignCarrierAwb,
        is_recommended: !isReassigning,
        ...(assignCarrier === "DELHIVERY_B2B" ? { delhivery_handoff_mode: assignHandoffMode } : {}),
        ...(isReassigning ? { override_reason: "Changed from booking-time selection", override_reason_text: assignReassignReason.trim() } : {}),
      };
      const res = await api.post(`/v2/assignments/${assignModal.id}/assign`, payload);
      const label = assignCarrier === "OTHERS" ? assignOthersText.trim() : (ASSIGN_CARRIER_OPTIONS.find(o => o.value === assignCarrier)?.label || assignCarrier);
      toast.success(`Assigned to ${label} | AWB: ${res.data.awb || "pending"}`);
      setAssignModal(null);
      fetchShipments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assignment failed");
    } finally {
      setAssigning(false);
    }
  };
  const [handoffMode, setHandoffMode] = useState("FM_PICKUP");
  const [nearestCenter, setNearestCenter] = useState(null);
  const [nearestCenterLoading, setNearestCenterLoading] = useState(false);

  const openEditAmount = (s) => {
    setEditAmountShipment(s);
    setEditAmountValue(String(s.bill_amount_override || s.price || ""));
    setEditAmountRemark("");
  };

  const submitEditAmount = async () => {
    const newAmount = parseFloat(editAmountValue);
    if (isNaN(newAmount) || newAmount < 0) return toast.error("Enter a valid amount");
    const oldAmount = editAmountShipment.bill_amount_override || editAmountShipment.price || 0;
    if (newAmount < oldAmount && !editAmountRemark.trim()) return toast.error("Enter a reason for the discount");
    setEditAmountSubmitting(true);
    try {
      await api.put(`/v2/payments/shipments/${editAmountShipment.id}/edit-amount`, {
        new_amount: newAmount,
        remark: editAmountRemark || undefined,
      });
      toast.success("Amount updated");
      setEditAmountShipment(null);
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update amount");
    } finally {
      setEditAmountSubmitting(false);
    }
  };

  const markCashPaid = async (s) => {
    setQrLoading(s.id);
    try {
      await api.post(`/v2/payments/shipments/${s.id}/mark-cash-paid`);
      toast.success("Marked as paid in cash");
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark as paid");
    }
    setQrLoading(null);
  };
  const displayAwb = (s) => {
    return s.awb || s.awb_number || "—";
  };
  // Same rule as the server: choosing "Delhivery B2B" always means the B2B (LR) manifest, whatever the weight.
  // Other Delhivery shipments: 20 kg and over go B2B, under 20 kg go parcel (AWB).
  const isB2bShipment = (s) => {
    const carrier = (s.assigned_carrier || "").toUpperCase();
    if (carrier === "DELHIVERY_B2B") return true;
    return (s.package?.weight || 0) >= 20;
  };
  const needsManifest = (s) => {
    const carrier = (s.assigned_carrier || "").toUpperCase();
    if (!carrier.startsWith("DELHIVERY")) return false;
    if (s.awb || s.awb_number || s.delhivery_lrnum) return false;
    if (isB2bShipment(s) && manifestJob(s) === "waiting") return false;
    return true;
  };
  // A job Delhivery accepted but never answered (or failed) must not block Manifest for ever: after 3 minutes it can be retried.
  const manifestJob = (s) => {
    if (s.delhivery_lrnum) return "done";
    const queued = s.delhivery_b2b_trigger_status === "queued" || s.delhivery_manifest_job_id;
    if (!queued) return "none";
    if (s.delhivery_manifest_status === "failed") return "failed";
    return tick - utcMs(s.delhivery_manifest_submitted_at) > 180000 ? "timed_out" : "waiting";
  };
  // The API sends times like "2026-10-18T18:23:53" with no zone; they are UTC. Without this, a browser in India reads them as local time (5.5 h earlier).
  const utcMs = (v) => {
    if (!v) return 0;
    const t = String(v);
    return new Date(/([zZ]|[+-]\d\d:?\d\d)$/.test(t) ? t : `${t}Z`).getTime() || 0;
  };
  // Which Delhivery pickup network a manifested shipment uses: "3PL_B2B" (LR number) | "3PL_B2C" (parcel AWB, B2C or C2C) | null
  const pickupNet = (s) => {
    const c = (s.assigned_carrier || "").toUpperCase();
    if (c === "DELHIVERY_B2B") return s.delhivery_lrnum ? "3PL_B2B" : null;
    if (c === "DELHIVERY_B2C" || c === "DELHIVERY_C2C") return (s.awb || s.awb_number) ? "3PL_B2C" : null;
    return null;
  };
  // Pickup state for a manifested Delhivery shipment: null (n/a) | "needed" | "raised" | "picked" | "cancelled"
  const pickupState = (s) => {
    const net = pickupNet(s);
    if (!net) return null;
    if (["cancelled", "canceled", "rto", "returned", "rto_delivered", "lost"].includes(String(s.status || "").toLowerCase())) return null;
    if (["picked_up", "in_transit", "out_for_delivery", "delivered"].includes(String(s.status || "").toLowerCase()) || s.pickup_status === "PICKED_UP") return "picked";
    if (s.pickup_raised || (s.pickup_network === net && s.pickup_status === "PICKUP_SCHEDULED")) return "raised";
    return s.pickup_cancelled ? "cancelled" : "needed";
  };
  // LR colour by pickup stage: red = pickup not raised / cancelled, orange = raised and waiting, blue = picked up (with a green tick)
  const lrColour = (s) => {
    const p = pickupState(s);
    return p === "needed" || p === "cancelled" ? "!text-red-600" : p === "raised" ? "!text-orange-500" : p === "picked" ? "!text-blue-600" : "";
  };
  // Delhivery B2B cancellation request (PNPR) - pre-filled PDF, dated today, with the company stamp
  const downloadPnpr = async (s, reasonOverride) => {
    try {
      let reason = reasonOverride;
      if (reason === undefined) {
        reason = s.cancelled_reason || window.prompt("Reason for the Delhivery cancellation request:", "Wrongly manifested - wrong pincode");
        if (reason === null) return;
      }
      const r = await api.get(`/v2/pnpr/${s.id}`, { params: { reason }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Delhivery-PNPR for LR-${s.delhivery_lrnum}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      let msg = "";
      try { const t = e?.response?.data; msg = t instanceof Blob ? JSON.parse(await t.text()).detail : t?.detail; } catch { /* keep generic */ }
      toast.error(`Could not create the PNPR form${e?.response?.status ? ` (${e.response.status})` : ""}${typeof msg === "string" && msg ? `: ${msg}` : ""}`);
    }
  };
  // Once a shipment is booked onto a carrier / manifested, its payment mode is locked.
  const payLocked = (s) => !!(s.assigned_carrier || s.awb || s.awb_number || s.delhivery_lrnum || !["", "booked", "draft"].includes(String(s.status || "").toLowerCase()));
  const manifestWaitLeft = (s) => Math.max(0, Math.ceil((180000 - (tick - utcMs(s.delhivery_manifest_submitted_at))) / 1000));
   const needsB2bRetry = (s) => s.delhivery_b2b_trigger_status === "failed_needs_retry" && !s.delhivery_lrnum;
  // Business (GSTIN) customers are invoiced at month end: per shipment we show "due month-end" + a manual Generate invoice button.
  const [monthlyFlags, setMonthlyFlags] = useState({});
  useEffect(() => {
    const ids = shipments.map((x) => x.id).filter((id) => id && monthlyFlags[id] === undefined);
    if (!ids.length) return;
    api.post("/v2/billing-mode/shipments", { shipment_ids: ids })
      .then((r) => setMonthlyFlags((m) => ({ ...m, ...r.data })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipments]);
  const [acctTypes, setAcctTypes] = useState({});
  useEffect(() => {
    const ids = shipments.map((x) => x.id).filter((id) => id && !acctTypes[id]);
    if (!ids.length) return;
    api.post("/v2/shipment-docs/account-types", { shipment_ids: ids })
      .then((r) => setAcctTypes((p) => ({ ...p, ...r.data })))
      .catch(() => { /* badge is optional */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipments]);
  const AcctBadge = ({ s }) => {
    const a = acctTypes[s.id];
    if (!a) return null;
    const cfg = { WALKIN: ["Walk-in", "bg-slate-200 text-slate-700"], CREDIT: ["Credit", "bg-violet-100 text-violet-700"], WALLET: ["Wallet", "bg-sky-100 text-sky-700"] }[a.type];
    const tip = a.type === "WALKIN" ? `Walk-in customer - pays at the counter (${a.payment_status || "pending"})`
      : `${a.type === "CREDIT" ? "Credit customer" : "Wallet customer"}: wallet Rs ${a.wallet_balance}` + (a.type === "CREDIT" ? ` + credit limit Rs ${a.credit_limit} = Rs ${a.available} available` : "") + ` - payment: ${a.payment_status || "pending"}`;
    return <span title={tip} data-testid={`acct-type-${s.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${cfg[1]}`}>{cfg[0]}</span>;
  };
  const CarrierBadge = ({ s }) => {
    const c = (s.assigned_carrier || "").toUpperCase();
    const map = { DELHIVERY_B2B: ["Delhivery B2B", "bg-red-100 text-red-700"], DELHIVERY_B2C: ["Delhivery B2C", "bg-orange-100 text-orange-700"], DELHIVERY_C2C: ["Delhivery B2C", "bg-orange-100 text-orange-700"], OWN_NETWORK: ["Own Network", "bg-emerald-100 text-emerald-700"] };
    const cfg = map[c] || (c ? [c.replace(/_/g, " "), "bg-slate-100 text-slate-700"] : null);
    if (!cfg) return null;
    const lr = s.delhivery_lrnum ? ` - LR ${s.delhivery_lrnum}` : "";
    return <span title={`Connected through ${cfg[0]}${lr}`} data-testid={`carrier-badge-${s.id}`} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg[1]}`}>{cfg[0]}</span>;
  };
  const [reviewShipment, setReviewShipment] = useState(null);
  const [manifestPopup, setManifestPopup] = useState(null);
  const [pickupShipment, setPickupShipment] = useState(null);
  const [cancelShipment, setCancelShipment] = useState(null);
  const [paySwitchShipment, setPaySwitchShipment] = useState(null);
  const [tick, setTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setTick(Date.now()), 5000); return () => clearInterval(t); }, []);
  const [editShipmentId, setEditShipmentId] = useState(null);
  const triggerManifest = async (s) => {
       setManifestingId(s.id);
       try {
         const isB2b = isB2bShipment(s);
         const endpoint = isB2b ? `/v2/assignments/${s.id}/manifest-b2b` : `/v2/assignments/${s.id}/manifest`;
         const r = await api.post(endpoint);
         toast.success(isB2b ? "B2B manifest queued — real LR/AWB will arrive shortly" : `Manifested! AWB: ${r.data.awb}`);
         fetchShipments();
       } catch (e) {
         const d = e.response?.data?.detail;
         if (d && typeof d === "object") { setManifestPopup(d); fetchShipments(); }
         else toast.error(d || "Manifestation failed — please retry");
       } finally {
         setManifestingId(null);
       }
     };
  const lookupNearestCenter = async (pincode) => {
    if (!pincode) return;
    setNearestCenterLoading(true);
    setNearestCenter(null);
    try {
      const r = await api.get(`/v2/vendor-api/delhivery/nearest-center?pincode=${pincode}`);
      setNearestCenter(r.data);
    } catch (e) {
      setNearestCenter({ error: true });
    } finally {
      setNearestCenterLoading(false);
    }
  };
  const openActions = (s) => {
    setHandoffMode(s.delhivery_handoff_mode === "SELF_DROP" ? "SELF_DROP" : "FM_PICKUP");
    setNearestCenter(null);
    setActionsShipment(s);
  };
  const manifestWithHandoffMode = async (s) => {
    if (s.assigned_carrier === "DELHIVERY_B2B" && handoffMode !== s.delhivery_handoff_mode) {
      try {
        await api.patch(`/v2/assignments/${s.id}/handoff-mode`, { delhivery_handoff_mode: handoffMode });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not update handoff mode — manifest not triggered");
        return;
      }
    }
    setReviewShipment(s); // nothing is sent to Delhivery until the review is approved
  };
  const showPaymentQr = async (s) => {
        setQrLoading(s.id);
        try {
          const r = await api.post(`/v2/payments/shipments/${s.id}/payment-link`);
          setQrModal({ url: r.data.link_url, orderId: s.order_id, linkId: r.data.link_id });
        } catch (e) {
          toast.error(e.response?.data?.detail || "Could not create payment link");
        }
        setQrLoading(null);
      };

  useEffect(() => {
    if (!qrModal?.linkId) return;
    const interval = setInterval(async () => {
      try {
        const statusRes = await api.get(`/v2/payments/links/${qrModal.linkId}/status`);
        if (statusRes.data.status === "PAID") {
          clearInterval(interval);
          toast.success("Payment received!");
          setQrModal(null);
          fetchShipments();
        }
      } catch (e) { /* transient errors — keep polling */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [qrModal?.linkId]);
    useEffect(() => { fetchShipments(); }, [statusFilter, segmentFilter, networkFilter, nddLate]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (segmentFilter !== "all") params.customer_segment = segmentFilter;
      if (networkFilter !== "all") params.network = networkFilter;
      if (nddLate) params.ndd = "late";
      if (search) params.search = search;
      const res = await api.get("/shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  // While a manifest is waiting for Delhivery, re-read the list quietly every 5 s so the LR (or the failure)
  // appears by itself - no spinner, no hard refresh.
  const refreshQuietly = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (segmentFilter !== "all") params.customer_segment = segmentFilter;
      if (networkFilter !== "all") params.network = networkFilter;
      if (nddLate) params.ndd = "late";
      if (search) params.search = search;
      const res = await api.get("/shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* try again on the next tick */ }
  };
  const anyWaiting = shipments.some((s) => manifestJob(s) === "waiting");
  useEffect(() => { if (anyWaiting) refreshQuietly(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">All Shipments</h1>
          <p className="text-slate-500 text-sm">{total} total shipments</p>
        </div>

        {selectedForCombine.size >= 2 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="combine-action-bar">
            <span className="text-sm font-semibold">{selectedForCombine.size} shipments selected</span>
            {!sameCustomer ? (
              <span className="text-xs text-amber-300" title="All selected shipments must belong to the same customer">
                ⚠️ This shipment belongs to a different customer — please uncheck it before combining
              </span>
            ) : (
              <button
                onClick={handleCombineClick}
                disabled={combining}
                data-testid="combine-into-invoice-btn"
                className="px-4 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
              >
                {combining ? "Combining..." : "Combine into Invoice"}
              </button>
            )}
            <button onClick={() => setSelectedForCombine(new Set())} className="text-slate-400 hover:text-white text-xs">
              Clear
            </button>
          </div>
        )}

        {combineResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="combine-result-panel">
            <div>
              <p className="text-emerald-800 font-semibold text-sm">
                Combined into {combineResult.invoices?.length > 1 ? `${combineResult.invoices.length} invoices` : "1 invoice"} ({combineResult.combined_ref})
              </p>
              {combineResult.invoices?.length > 1 && (
                <p className="text-emerald-700 text-xs mt-0.5">Split by GST tax type — each keeps its own compliant invoice number.</p>
              )}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {(combineResult.invoices || []).map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => downloadCombinedFile(`${process.env.REACT_APP_BACKEND_URL}/api/v2/invoices/${inv.id}/pdf`, `Sendtoo_${inv.invoice_number}.pdf`)}
                  className="px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-100"
                >
                  Download {inv.invoice_number}
                </button>
              ))}
              {combineResult.payment_link_url && (
                <a
                  href={combineResult.payment_link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700"
                >
                  Payment Link
                </a>
              )}
              <button onClick={() => setCombineResult(null)} className="text-emerald-500 hover:text-emerald-700 text-xs px-2">✕</button>
            </div>
          </div>
        )}

        <NddWatchBanner active={nddLate} onToggle={() => setNddLate((v) => !v)} />

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); fetchShipments(); }} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tracking ID, city..."
                data-testid="admin-shipments-search"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
            </div>
            <button type="submit" className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm">Search</button>
          </form>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTIONS.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`admin-filter-${s}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors ${statusFilter === s ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {s === "all" ? "All" : s.replace(/_/g, " ")}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap border-l border-slate-200 pl-3" data-testid="network-filter">
            {[["all", "All networks"], ["own", "Own network"], ["3pl", "3PL"]].map(([val, lbl]) => (
              <button key={val} onClick={() => setNetworkFilter(val)} data-testid={`admin-network-${val}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${networkFilter === val ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {lbl}
              </button>
            ))}
            <button onClick={() => setNddLate((v) => !v)} data-testid="admin-ndd-late"
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${nddLate ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"}`}>
              NDD late
            </button>
          </div>
          <div className="flex gap-2 flex-wrap border-l border-slate-200 pl-3">
            {["all", "WALKIN", "WALLET", "CREDIT"].map((seg) => (
              <button key={seg} onClick={() => setSegmentFilter(seg)} data-testid={`admin-segment-${seg}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors ${segmentFilter === seg ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {seg === "all" ? "All Segments" : seg}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center"><Package className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">No shipments found.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <div className="space-y-3 p-3 font-mont">
                {shipments.map((s) => (
                  <div key={s.id} data-testid="admin-all-shipment-row" className={`p-4 bg-white border rounded-xl shadow-sm hover:shadow-md transition-shadow ${selectedForCombine.has(s.id) ? "border-brand-400 ring-2 ring-brand-100" : "border-slate-200"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      {/* Combine checkbox removed: invoice combining is automated. */}
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 mb-1.5">
                          <button
                            onClick={() => navigate(`/admin/shipments/${s.id}/tracking`)}
                            data-testid={`admin-tracking-link-${s.tracking_id}`}
                            className="font-heading font-bold text-brand-600 text-sm hover:underline"
                          >
                            {s.order_id || s.tracking_id}
                          </button>
                          <AcctBadge s={s} />
                          <CarrierBadge s={s} />
                          {s.booked_via === "CUSTOMER_PORTAL" && (
                            <span title={`Booked by the customer in the portal${s.handover_mode === "DROP" ? " - will drop at the counter" : s.requested_pickup_date ? ` - pickup requested ${s.requested_pickup_date} ${String(s.requested_pickup_slot || "").toLowerCase()}` : ""}`}
                              data-testid={`portal-badge-${s.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 cursor-help">
                              By customer{s.handover_mode === "DROP" ? " · counter drop" : ""}</span>
                          )}
                          {s.bill_to?.gstin && !s.bill_to?.verified && (
                            <button type="button" onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Bill-to GSTIN ${s.bill_to.gstin} (${s.bill_to.legal_name}) added by the customer.\n\nMark it VERIFIED? Check it on the GST portal first.`)) return;
                                try { await api.post(`/v2/my-booking/staff/billing-accounts/${s.bill_to.account_id}/verify`, { verified: true }); toast.success("GSTIN verified"); fetchShipments(); }
                                catch (err) { toast.error(err.response?.data?.detail || "Could not verify"); }
                              }}
                              title={`Invoice will be billed to ${s.bill_to.gstin} (${s.bill_to.legal_name}) - not verified yet. Click to verify.`}
                              data-testid={`gstin-unverified-${s.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200">
                              GSTIN {s.bill_to.gstin.slice(0, 2)} unverified</button>
                          )}
                          {s.eway_bill_required && !s.eway_bill_verified && (
                            <span title="Eway Bill required — not yet added" data-testid={`eway-warn-${s.tracking_id}`} className="text-amber-600 text-xs">⚠️</span>
                          )}
                          {s.eway_bill_verified && (
                            <span title="Eway Bill verified" data-testid={`eway-ok-${s.tracking_id}`} className="text-emerald-600 text-xs font-bold">✓</span>
                          )}
                          {s.service_level && (
                            <span
                              title={s.service_level === "NDD" ? "Next Day Delivery" : s.service_level === "EXPRESS" ? "Express" : "Standard"}
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                                s.service_level === "NDD" ? "bg-red-100 text-red-700"
                                : s.service_level === "EXPRESS" ? "bg-amber-100 text-amber-700"
                                : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {s.service_level}
                            </span>
                          )}
                        </div>
                        <div className="mb-2">
                          {needsManifest(s) || needsB2bRetry(s) ? (
                            <button
                              onClick={() => openActions(s)}
                              disabled={manifestingId === s.id}
                              data-testid={`manifest-btn-${s.id}`}
                              className="text-xs font-bold px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {manifestingId === s.id ? "Manifesting..." : (manifestJob(s) === "none" ? "Manifest" : "Retry manifest")}
                            </button>
                          ) : manifestJob(s) === "waiting" && s.assigned_carrier?.startsWith("DELHIVERY") ? (
                            <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`manifest-wip-${s.id}`}>
                              Sent to Delhivery - waiting for LR (retry in {Math.floor(manifestWaitLeft(s) / 60)}:{String(manifestWaitLeft(s) % 60).padStart(2, "0")})
                            </span>
                          ) : is3pl(s) && (s.awb || s.awb_number) ? (
                            <button
                              onClick={() => setTransitAwb(s.awb || s.awb_number)}
                              className={`hover:underline font-semibold font-mono text-xs ${
                                s.assigned_carrier === "DELHIVERY_B2B" && s.b2b_weight_reconciled
                                  ? "text-purple-600"
                                  : "text-indigo-600"
                              } ${lrColour(s)}`}
                              title={
                                s.assigned_carrier === "DELHIVERY_B2B" && s.b2b_weight_reconciled
                                  ? "Weight updated via Delhivery reconciliation"
                                  : undefined
                              }
                              data-testid={`awb-link-${s.id}`}
                            >
                              {s.assigned_carrier === "DELHIVERY_B2B" && s.delhivery_lrnum
                                ? <>{`LR: ${s.delhivery_lrnum}`}{pickupState(s) === "picked" && <span className="text-emerald-600 ml-1" title="Picked up">✓</span>}</>
                                : <>{`AWB: ${displayAwb(s)}`}{pickupState(s) === "picked" && <span className="text-emerald-600 ml-1" title="Picked up">✓</span>}</>}
                            </button>
                          ) : (
                            <span className="font-mono text-xs text-slate-500">{displayAwb(s)}</span>
                          )}
                        </div>
                        {pickupState(s) === "cancelled" && (
                          <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid={`pickup-cancelled-${s.id}`}>
                            <span className="text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" title={s.pickup_cancelled?.reason}>
                              Pickup cancelled{s.pickup_cancelled?.reason ? ` - ${s.pickup_cancelled.reason}` : ""}
                            </span>
                            <button onClick={() => setPickupShipment(s)} data-testid={`recreate-pickup-${s.id}`}
                              className="text-xs font-bold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">Recreate pickup</button>
                          </div>
                        )}
                        {pickupState(s) === "needed" && (
                          <div className="mb-2">
                            <button onClick={() => setPickupShipment(s)} data-testid={`raise-pickup-${s.id}`}
                              className="text-xs font-bold px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">Raise pickup</button>
                          </div>
                        )}
                        {pickupState(s) === "raised" && (
                          <div className="mb-2 flex items-center gap-1" data-testid={`pickup-raised-${s.id}`}>
                            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                              Pickup raised ✓{s.pickup_raised?.pickup_date ? ` ${s.pickup_raised.pickup_date} ${String(s.pickup_raised.slot || "").toLowerCase()}` : ""}{(s.pickup_raised?.delhivery_pur_number || s.delhivery_pur_number) ? ` · #${s.pickup_raised?.delhivery_pur_number || s.delhivery_pur_number}` : ""}
                            </span>
                            <button onClick={() => setCancelShipment(s)} title="Cancel this pickup" aria-label="Cancel pickup" data-testid={`cancel-pickup-${s.id}`}
                              className="w-5 h-5 flex items-center justify-center rounded-full text-red-600 hover:bg-red-100 font-bold text-sm leading-none">✕</button>
                          </div>
                        )}
                        {s.assigned_carrier === "DELHIVERY_B2B" && s.delhivery_lrnum && String(s.status || "").toLowerCase() === "cancelled" && (
                          <div className="mb-2"><button onClick={() => downloadPnpr(s)} data-testid={`pnpr-${s.id}`}
                            className="text-[11px] font-semibold text-slate-500 underline hover:text-slate-800">Download PNPR (cancel request PDF)</button></div>
                        )}
                        {pickupState(s) === "picked" && (
                          <div className="mb-2"><span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">Picked up ✓</span></div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-start gap-2">
                            <span className="text-pink-500 mt-0.5">📍</span>
                            <div>
                              <div className="text-xs font-semibold text-slate-700">{s.origin?.name || "—"}</div>
                              <div className="text-xs text-slate-500">{s.origin?.city}</div>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-emerald-600 mt-0.5">🏁</span>
                            <div>
                              <div className="text-xs font-semibold text-slate-700">{s.destination?.name || "—"}</div>
                              <div className="text-xs text-slate-500">{s.destination?.city}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Weight</div>
                          {s.b2b_weight_reconciled ? (
                            <div>
                              <div className="font-semibold text-purple-600 text-sm">
                                {s.chargeable_weight ?? s.package?.weight}kg
                              </div>
                              <div className="text-[10px] text-slate-400">
                                was {s.b2b_weight_pre_reconciliation ?? s.package?.weight}kg
                              </div>
                            </div>
                          ) : (
                            <div className="font-semibold text-slate-700 text-sm">
                              {s.chargeable_weight ?? s.package?.weight}kg
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Boxes</div>
                          <div className="font-semibold text-slate-700 text-sm">{s.box_count || 1}</div>
                        </div>
                        <div>
                          <div className="text-slate-400 uppercase text-[10px] font-bold mb-0.5">Price</div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-slate-700 text-sm">₹{s.final_locked ? s.bill_amount_override : s.price}</span>
                            {s.final_locked && <span title="Final bill locked" data-testid={`final-chip-${s.id}`} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">FINAL</span>}
                            {s.payment_status !== "paid" && !s.final_locked && (
                              <button
                                onClick={() => openEditAmount(s)}
                                title="Edit amount"
                                className="text-slate-400 hover:text-brand-600 text-xs"
                              >
                                ✏️
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 min-w-[110px]">
                        {s.combined_ref ? (() => {
                          // Real fix (Sep 5 2026): a real Cashfree payment
                          // link IS generated at combine time for the
                          // unpaid total (stored in db.payment_links keyed
                          // by combined_ref) -- it was just never surfaced
                          // anywhere in the UI, so staff had no way to
                          // collect payment on a merged group at all.
                          // Mirrors the existing WALKIN primary-shipment
                          // pattern: only the shipment with the highest
                          // order_id in the group shows the real payment
                          // action; the rest just show the merged badge.
                          const group = shipments.filter((x) => x.combined_ref === s.combined_ref);
                          const maxOrderId = group.reduce((max, x) => (x.order_id > max ? x.order_id : max), group[0]?.order_id || "");
                          const isPrimaryInGroup = s.order_id === maxOrderId;

                          if (!isPrimaryInGroup || s.payment_status === "paid") {
                            return (
                              <span
                                title={`This shipment's invoice was merged with others into ${s.combined_ref}`}
                                data-testid={`merged-badge-${s.id}`}
                                className="text-xs font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 text-center cursor-help"
                              >
                                Merged into {s.combined_ref}
                              </span>
                            );
                          }

                          return (
                            <button
                              onClick={async () => {
                                try {
                                  const res = await api.get(`/v2/payments/combined/${s.combined_ref}/link`);
                                  if (res.data?.link_url) window.open(res.data.link_url, "_blank");
                                  else toast.error("No active payment link found for this combine");
                                } catch {
                                  toast.error("Could not fetch payment link");
                                }
                              }}
                              title={`Merged into ${s.combined_ref} -- send this link to collect the full combined amount`}
                              data-testid={`merged-payment-link-${s.id}`}
                              className="text-xs font-bold px-2 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 text-center"
                            >
                              Merged — Send Payment Link
                            </button>
                          );
                        })() : (s.payment_status === "paid" && Number(s.credit_amount) > 0 && !s.credit_settled) ? (
  <span title={`Charged to the credit limit - Rs ${s.credit_amount} still owed; cleared when the month-end bank transfer is marked paid`}
    data-testid={`on-credit-${s.id}`} className="text-xs font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 text-center cursor-help">
    On credit ₹{s.credit_amount}
  </span>
) : s.payment_status === "paid" ? (
  <span className="text-xs font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-center">
    {s.payment_mode === "cash" || s.payment_mode === "ppp_cash" ? "Paid in Cash" : "Received"}
  </span>
) : s.payment_mode === "ppp_cash" ? (
  <button
    onClick={() => markCashPaid(s)}
    disabled={qrLoading === s.id}
    data-testid={`ppp-cash-status-btn-${s.id}`}
    title="Pay Post Pickup — shipment connected without waiting for payment"
    className="text-xs font-bold px-2 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
  >
    {qrLoading === s.id ? "..." : "PPP — Mark Paid (Cash)"}
  </button>
) : s.payment_mode === "ppp_upi" ? (
  <button
    onClick={() => showPaymentQr(s)}
    disabled={qrLoading === s.id}
    data-testid={`ppp-upi-status-btn-${s.id}`}
    title="Pay Post Pickup — shipment connected without waiting for payment"
    className="text-xs font-bold px-2 py-0.5 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
  >
    {qrLoading === s.id ? "..." : "PPP — Send Link"}
  </button>
) : s.payment_mode === "cash" ? (
  <button
    onClick={() => markCashPaid(s)}
    disabled={qrLoading === s.id}
    data-testid={`cash-status-btn-${s.id}`}
    className="text-xs font-bold px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
  >
    {qrLoading === s.id ? "..." : "Mark Paid (Cash)"}
  </button>
) : (
  <button
    onClick={() => showPaymentQr(s)}
    disabled={qrLoading === s.id}
    data-testid={`payment-status-btn-${s.id}`}
    className="text-xs font-bold px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
  >
    {qrLoading === s.id ? "..." : "Pending"}
  </button>
)}
                        {!s.combined_ref && String(s.status || "").toLowerCase() !== "cancelled" && !payLocked(s) && (
                          <button onClick={() => setPaySwitchShipment(s)} data-testid={`change-payment-${s.id}`}
                            title="Change how this order is paid (wallet / cash / pay post pickup)"
                            className="text-[11px] font-semibold text-slate-500 underline hover:text-slate-800 text-left">
                            Change payment
                          </button>
                        )}
                        {s.customer_segment === "WALKIN" && (() => {
                          // Merged-group primary logic (Aug 25 2026): among
                          // WALKIN shipments sharing the same combined_ref,
                          // only the one with the highest order_id keeps a
                          // clickable invoice button -- the rest show their
                          // real invoice number, disabled, for clarity.
                          // Payment status badge above is untouched either way.
                          let isPrimaryInGroup = true;
                          if (s.combined_ref) {
                            const group = shipments.filter((x) => x.combined_ref === s.combined_ref);
                            const maxOrderId = group.reduce((max, x) => (x.order_id > max ? x.order_id : max), group[0]?.order_id || "");
                            isPrimaryInGroup = s.order_id === maxOrderId;
                          }

                          if (s.combined_ref && !isPrimaryInGroup) {
                            return (
                              <span
                                title={`Merged into ${s.combined_ref} -- view/download from the primary shipment in this group`}
                                className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-center cursor-not-allowed"
                              >
                                {s.invoice_number || s.combined_ref}
                              </span>
                            );
                          }

                          return s.invoice_document ? (
                            <button onClick={() => viewInvoice(s)} disabled={invoiceLoading === s.id}
                              className="text-emerald-600 hover:underline font-semibold text-xs text-center bg-transparent disabled:opacity-50">
                              {invoiceLoading === s.id ? "..." : (s.invoice_number ? `Invoice ${s.invoice_number}` : "Invoice ✓")}
                            </button>
                          ) : (
                            <button onClick={() => raiseInvoice(s)} disabled={invoiceLoading === s.id}
                              data-testid={`raise-invoice-${s.id}`}
                              className="text-xs font-bold px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                              {invoiceLoading === s.id ? "..." : "Raise Invoice"}
                            </button>
                          );
                        })()}
                        {s.customer_segment !== "WALKIN" && monthlyFlags[s.id] && String(s.status || "").toLowerCase() !== "cancelled" && (
                          s.invoice_document ? (
                            <button onClick={() => viewInvoice(s)} disabled={invoiceLoading === s.id}
                              className="text-emerald-600 hover:underline font-semibold text-xs text-center bg-transparent disabled:opacity-50">
                              {invoiceLoading === s.id ? "..." : (s.invoice_number ? `Invoice ${s.invoice_number}` : "Invoice ✓")}
                            </button>
                          ) : (
                            <div className="flex flex-col items-center gap-1" data-testid={`monthly-invoice-${s.id}`}>
                              <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5" title="Business customers are invoiced together at month end">Invoice due month-end</span>
                              <button onClick={() => raiseInvoice(s)} disabled={invoiceLoading === s.id} data-testid={`generate-invoice-${s.id}`}
                                className="text-[11px] font-bold px-2 py-0.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                                {invoiceLoading === s.id ? "..." : "Generate invoice now"}
                              </button>
                            </div>
                          )
                        )}
                        {s.customer_invoice_number && (
                          <span className="block text-center text-xs text-slate-600" data-testid={`inv-no-${s.id}`}>Inv no: <b className="font-mono">{s.customer_invoice_number}</b></span>
                        )}
                        {(s.eway_bill_number || s.eway_bill_document) && (
                          <span className="flex items-center justify-center gap-1.5 text-xs" data-testid={`eway-no-${s.id}`}>
                            <span className="text-amber-700 font-semibold">E-way{s.eway_bill_number ? ` ${s.eway_bill_number}` : ""}</span>
                            {s.eway_bill_document && (<>
                              <button onClick={() => viewEway(s)} data-testid={`view-eway-${s.id}`} className="text-brand-600 hover:underline bg-transparent">View</button>
                              <button onClick={() => viewEway(s, true)} data-testid={`dl-eway-${s.id}`} className="text-brand-600 hover:underline bg-transparent">Download</button>
                            </>)}
                          </span>
                        )}
                        {(s.assigned_carrier === "DELHIVERY_B2B") && (() => {
                          if (s.delivery_challan_document) {
                            return (
                              <button onClick={() => viewDC(s)} disabled={dcLoading === s.id}
                                data-testid={`view-dc-${s.id}`}
                                className="text-teal-600 hover:underline font-semibold text-xs text-center bg-transparent disabled:opacity-50 block">
                                {dcLoading === s.id ? "..." : `DC ${s.delivery_challan_document.dc_number || "✓"}`}
                              </button>
                            );
                          }
                          return (
                            <div className="flex gap-1 mt-1">
                              <button onClick={() => setDcUploadModal(s)}
                                data-testid={`upload-dc-${s.id}`}
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700">
                                Upload DC
                              </button>
                              <button onClick={() => generateDC(s)} disabled={dcLoading === s.id}
                                data-testid={`generate-dc-${s.id}`}
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-50">
                                {dcLoading === s.id ? "..." : "Generate"}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex flex-col gap-1.5 items-start">
                        <StatusBadge status={s.current_status === "3PL_NDR" ? "ndr" : s.status} />
                        {!s.assigned_carrier && (s.status === "booked" || s.status === "manifested") && (
                          <button
                            onClick={() => openAssignModal(s)}
                            data-testid={`assign-btn-${s.id}`}
                            className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 shadow-sm"
                          >
                            Assign
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date(s.created_at).toLocaleDateString("en-IN")}
                      </div>
                      <div className="flex gap-1">
                        {(s.status === "booked" || s.status === "manifested") && (
                          <button
                            onClick={() => { setCancelModal(s); setCancelReason(""); }}
                            title="Cancel shipment"
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            data-testid={`cancel-btn-${s.id}`}
                          >
                            ✕
                          </button>
                        )}
                        {String(s.status || "").toLowerCase() === "cancelled" && (
                          <button onClick={() => navigate(`/admin/book?clone=${s.id}`)} title="Clone this shipment as a new draft order" data-testid={`clone-${s.id}`}
                            className="px-2 py-1 text-[11px] font-semibold text-indigo-600 border border-indigo-200 rounded hover:bg-indigo-50">Clone shipment</button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/shipments/${s.id}/tracking`)}
                          title="Tracking timeline"
                          className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                          data-testid={`track-icon-${s.id}`}
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        {(s.awb || s.awb_number) && (
                          <button
                            onClick={() => downloadLabel(s)}
                            title="Download label"
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                            data-testid={`label-icon-${s.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        {is3pl(s) && (
                          <button
                            onClick={() => sync3PL(s)}
                            disabled={syncingId === s.id}
                            title="Sync 3PL"
                            className="p-1.5 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded disabled:opacity-50"
                            data-testid={`sync-icon-${s.id}`}
                          >
                            {syncingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                        )}
                        {canEditShipment(s) && <button
                          onClick={() => setEditShipmentId(s.id)}
                          title="Edit shipment details (boxes, weight, address)"
                          data-testid={`edit-shipment-${s.id}`}
                          className="px-2 py-1 text-xs font-semibold text-indigo-700 border border-indigo-200 rounded hover:bg-indigo-50"
                        >
                          Edit
                        </button>}
                        <button
                          onClick={() => openActions(s)}
                          title="Shipment actions"
                          className="p-1.5 text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 rounded"
                          data-testid={`shipment-actions-icon-${s.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
<DelhiveryTransitModal awb={transitAwb} open={!!transitAwb} onClose={() => setTransitAwb(null)} />
    {qrModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setQrModal(null)}>
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-700">Payment — Order {qrModal.orderId}</h3>
            <span className="text-xs text-slate-400 animate-pulse">● Waiting for payment...</span>
          </div>
          <iframe
            src={qrModal.url}
            title="Cashfree Payment"
            className="w-full rounded-lg border border-slate-200"
            style={{ height: "480px" }}
          />
          <button onClick={() => { navigator.clipboard.writeText(qrModal.url); toast.success("Link copied"); }}
            className="w-full mt-3 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200">
            Copy Link
          </button>
          <button onClick={() => setQrModal(null)}
            className="w-full mt-2 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700">
            Close
          </button>
        </div>
      </div>
    )}
    {actionsShipment && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setActionsShipment(null)}>
        <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-sm font-bold text-slate-700 mb-3">Shipment Actions — {actionsShipment.order_id || actionsShipment.tracking_id}</h3>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Status</span>
              <StatusBadge status={actionsShipment.current_status === "3PL_NDR" ? "ndr" : actionsShipment.status} />
            </div>
            <div className="flex justify-between text-xs items-center">
              <span className="text-slate-500">Carrier</span>
              <span className="font-semibold text-slate-700">{actionsShipment.assigned_carrier || "—"}</span>
            </div>
            <div className="flex justify-between text-xs items-center">
              <span className="text-slate-500">AWB / LR</span>
              <span className="font-mono text-slate-700">{actionsShipment.delhivery_lrnum || actionsShipment.awb || actionsShipment.awb_number || "—"}</span>
            </div>
          </div>
          {(needsManifest(actionsShipment) || needsB2bRetry(actionsShipment)) && (
            <>
              {actionsShipment.assigned_carrier === "DELHIVERY_B2B" && (
                <div className="mb-3">
                  <HandoffModeToggle
                    handoffMode={handoffMode}
                    onHandoffModeChange={(mode) => { setHandoffMode(mode); if (mode === "FM_PICKUP") setNearestCenter(null); }}
                    onSelfDropSelected={() => lookupNearestCenter(actionsShipment?.origin?.pincode)}
                    nearestCenter={nearestCenter}
                    nearestCenterLoading={nearestCenterLoading}
                    testIdPrefix="actions-handoff"
                    radioGroupName="actions_handoff_mode"
                  />
                </div>
              )}
              <button
                onClick={() => { manifestWithHandoffMode(actionsShipment); setActionsShipment(null); }}
                disabled={manifestingId === actionsShipment.id}
                data-testid={`shipment-actions-manifest-${actionsShipment.id}`}
                className="w-full mb-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {manifestingId === actionsShipment.id ? "Manifesting..." : (manifestJob(actionsShipment) === "none" ? "Manifest" : "Retry manifest")}
              </button>
              {actionsShipment.delhivery_manifest_error && manifestJob(actionsShipment) !== "done" && (
                <div className="mb-2 text-xs rounded border border-red-200 bg-red-50 text-red-800 p-2" data-testid="manifest-last-error">
                  <b>Last attempt failed:</b> {actionsShipment.delhivery_manifest_error}
                  {actionsShipment.delhivery_manifest_error_raw && <div className="mt-1 text-[11px] text-red-600 break-words">Delhivery said: {actionsShipment.delhivery_manifest_error_raw}</div>}
                </div>
              )}
            </>
          )}
          {String(actionsShipment.status || "").toLowerCase() !== "cancelled" && (
            <button onClick={() => { setFinaliseShipment(actionsShipment); setActionsShipment(null); }}
              data-testid={`shipment-actions-finalise-${actionsShipment.id}`}
              className="w-full mb-2 px-4 py-2 border border-indigo-300 text-indigo-700 text-sm font-semibold rounded-lg hover:bg-indigo-50">
              {actionsShipment.final_locked ? `Final bill ₹${actionsShipment.bill_amount_override} (locked) / Raise invoice` : "Finalise bill (amount & weight)"}
            </button>
          )}
          {canEditShipment(actionsShipment) && <button onClick={() => { setEditShipmentId(actionsShipment.id); setActionsShipment(null); }}
            data-testid={`shipment-actions-edit-${actionsShipment.id}`}
            className="w-full mb-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50">
            Edit shipment details (boxes, weight, contacts)
          </button>}
          <button onClick={() => setActionsShipment(null)}
            className="w-full mt-2 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700">
            Close
          </button>
        </div>
      </div>
    )}
    {pickupShipment && (
      <PickupRaiseDialog shipment={pickupShipment} onClose={() => setPickupShipment(null)}
        onRaised={async () => { setPickupShipment(null); await fetchShipments(); }} />
    )}
    {paySwitchShipment && (
      <PaymentSwitchDialog shipment={paySwitchShipment} onClose={() => setPaySwitchShipment(null)}
        onDone={async () => { setPaySwitchShipment(null); await fetchShipments(); }} />
    )}
    {cancelShipment && (
      <PickupCancelDialog shipment={cancelShipment} onClose={() => setCancelShipment(null)}
        onCancelled={async () => { setCancelShipment(null); await fetchShipments(); }} />
    )}
    {manifestPopup && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setManifestPopup(null)}>
        <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="manifest-error-popup">
          <h3 className={`font-bold text-lg ${manifestPopup.code === "WALLET" ? "text-red-700" : "text-slate-800"}`}>
            {manifestPopup.code === "WALLET" ? "Recharge the Delhivery wallet" : (manifestPopup.title || "Manifest not sent")}
          </h3>
          <p className="text-sm text-slate-700 mt-2">{manifestPopup.message}</p>
          {manifestPopup.code === "WALLET" && <p className="text-xs text-slate-500 mt-2">After recharging, press Retry manifest. Nothing was created at Delhivery, so there is no duplicate risk.</p>}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setManifestPopup(null)} className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg">OK</button>
          </div>
        </div>
      </div>
    )}
    {reviewShipment && (
      <ManifestReviewModal shipment={reviewShipment} onClose={() => setReviewShipment(null)}
        onApproved={async () => { const sh = reviewShipment; setReviewShipment(null); await triggerManifest(sh); }} />
    )}
    {finaliseShipment && (
      <FinaliseBillDialog shipment={finaliseShipment} isAdmin={isAdminUser} onClose={() => setFinaliseShipment(null)}
        onDone={async () => { setFinaliseShipment(null); await fetchShipments(); }} />
    )}
    {editShipmentId && <ShipmentEditModal shipmentId={editShipmentId} onClose={() => setEditShipmentId(null)} onSaved={fetchShipments} />}
    {editAmountShipment && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditAmountShipment(null)}>
        <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-sm font-bold text-slate-700 mb-3">Edit Amount — {editAmountShipment.order_id}</h3>
          <label className="text-xs font-semibold text-slate-500 uppercase">New Amount (₹)</label>
          <input type="number" value={editAmountValue} onChange={(e) => setEditAmountValue(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          {parseFloat(editAmountValue) < (editAmountShipment.bill_amount_override || editAmountShipment.price || 0) && (
            <>
              <label className="text-xs font-semibold text-slate-500 uppercase mt-3 block">Reason for discount</label>
              <textarea value={editAmountRemark} onChange={(e) => setEditAmountRemark(e.target.value)} rows={2}
                placeholder="e.g. Loyalty discount, damaged packaging..."
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </>
          )}
          <button onClick={submitEditAmount} disabled={editAmountSubmitting}
            className="w-full mt-4 px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 disabled:opacity-50">
            {editAmountSubmitting ? "Saving..." : "Save Amount"}
          </button>
          <button onClick={() => setEditAmountShipment(null)}
            className="w-full mt-2 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700">
            Cancel
          </button>
        </div>
      </div>
    )}

    {assignModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="assign-carrier-modal">
        <div className="bg-white rounded-xl p-5 w-full max-w-md">
          <h3 className="font-heading font-bold text-slate-800 mb-3">Assign Carrier — {assignModal.order_id}</h3>

          <div className="space-y-2">
            {[...ASSIGN_CARRIER_OPTIONS].sort((a, b) => {
              const bookedNorm = normalizeConnectionThrough(assignModal.connection_through);
              const aBooked = a.value === bookedNorm;
              const bBooked = b.value === bookedNorm;
              if (aBooked && !bBooked) return -1;
              if (bBooked && !aBooked) return 1;
              return 0;
            }).map((opt) => {
              const isBookedChoice = opt.value === normalizeConnectionThrough(assignModal.connection_through);
              return (
                <label
                  key={opt.value}
                  data-testid={`assign-option-${opt.value}`}
                  className={`flex items-center gap-2 w-full text-left p-3 rounded-lg border text-sm cursor-pointer ${
                    isBookedChoice ? "border-emerald-400 bg-emerald-50"
                    : assignCarrier === opt.value ? "border-indigo-300 bg-indigo-50"
                    : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="assign_carrier"
                    checked={assignCarrier === opt.value}
                    onChange={() => setAssignCarrier(opt.value)}
                    className={isBookedChoice ? "accent-emerald-600" : "accent-indigo-600"}
                  />
                  <span className={`font-semibold ${isBookedChoice ? "text-emerald-700" : "text-slate-800"}`}>{opt.label}</span>
                  {isBookedChoice && <span className="text-[10px] font-bold text-emerald-600 uppercase ml-auto">Selected at booking</span>}
                </label>
              );
            })}
          </div>

          {assignCarrier === "OTHERS" && (
            <input
              value={assignOthersText}
              onChange={(e) => setAssignOthersText(e.target.value)}
              placeholder="Enter carrier name"
              data-testid="assign-others-text"
              className="w-full mt-3 border border-slate-300 rounded-lg p-2 text-sm"
            />
          )}

          {assignCarrier && assignCarrier !== "OWN_NETWORK" && !assignCarrier.startsWith("DELHIVERY") && (
            <input
              value={assignCarrierAwb}
              onChange={(e) => setAssignCarrierAwb(e.target.value)}
              placeholder="Carrier AWB number"
              className="w-full mt-3 border border-slate-300 rounded-lg p-2 text-sm"
            />
          )}

          {assignCarrier === "DELHIVERY_B2B" && (
            <div className="mt-3">
              <HandoffModeToggle
                handoffMode={assignHandoffMode}
                onHandoffModeChange={setAssignHandoffMode}
                onSelfDropSelected={() => lookupNearestCenter(assignModal?.origin?.pincode)}
                nearestCenter={nearestCenter}
                nearestCenterLoading={nearestCenterLoading}
                testIdPrefix="assign-handoff"
                radioGroupName="assign_handoff_mode"
              />
            </div>
          )}

          {showReassignConfirm && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg">
              <p className="text-xs text-amber-800 mb-2">
                You currently have <strong>{ASSIGN_CARRIER_OPTIONS.find(o => o.value === normalizeConnectionThrough(assignModal.connection_through))?.label || assignModal.connection_through}</strong> selected
                and are opting for <strong>{assignCarrier === "OTHERS" ? assignOthersText.trim() : (ASSIGN_CARRIER_OPTIONS.find(o => o.value === assignCarrier)?.label || assignCarrier)}</strong> instead.
                Are you sure you want to reassign?
              </p>
              <textarea
                value={assignReassignReason}
                onChange={(e) => setAssignReassignReason(e.target.value)}
                placeholder="Enter a remark for this network change (required)"
                data-testid="assign-reassign-reason"
                className="w-full border border-amber-300 rounded-lg p-2 text-sm h-16"
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button onClick={() => setAssignModal(null)} className="flex-1 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700">
              Cancel
            </button>
            <button
              onClick={submitAssign}
              disabled={assigning || !assignCarrier || (showReassignConfirm && !assignReassignReason.trim())}
              data-testid="confirm-assign-btn"
              className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {assigning ? "Assigning..." : showReassignConfirm ? "Confirm Reassignment" : "Confirm Assignment"}
            </button>
          </div>
        </div>
      </div>
    )}

    {cancelModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="cancel-shipment-modal">
        <div className="bg-white rounded-xl p-5 w-full max-w-sm">
          <h3 className="font-heading font-bold text-slate-800 mb-1">Cancel Shipment</h3>
          <p className="text-sm text-slate-500 mb-3">
            Do you really want to cancel <span className="font-semibold">{cancelModal.order_id}</span>? This cannot be undone -- a new shipment will need to be booked if this was a mistake.
          </p>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">Reason for cancellation *</label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter the reason for cancelling this shipment"
            data-testid="cancel-reason-input"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm h-20 focus:outline-none focus:border-red-400"
          />
          {cancelModal.assigned_carrier === "DELHIVERY_B2B" && cancelModal.delhivery_lrnum && (
            <label className="flex items-start gap-2 mt-3 text-xs text-slate-600" data-testid="cancel-pnpr-option">
              <input type="checkbox" checked={cancelPnpr} onChange={(e) => setCancelPnpr(e.target.checked)} className="mt-0.5" />
              <span>Also download the Delhivery cancellation request (PNPR) for LR {cancelModal.delhivery_lrnum}, stamped and signed, with this reason. Mail it to Delhivery to release the LR / e-way bill.</span>
            </label>
          )}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => { setCancelModal(null); setCancelReason(""); }}
              className="flex-1 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700"
            >
              Keep Shipment
            </button>
            <button
              onClick={handleCancelShipment}
              disabled={!cancelReason.trim() || cancelling}
              data-testid="confirm-cancel-btn"
              className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {cancelling ? "Cancelling..." : "Yes, Cancel Shipment"}
            </button>
          </div>
        </div>
      </div>
    )}

    {dcUploadModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="dc-upload-modal">
        <div className="bg-white rounded-xl p-5 w-full max-w-sm">
          <h3 className="font-heading font-bold text-slate-800 mb-1">Upload Delivery Challan</h3>
          <p className="text-sm text-slate-500 mb-3">
            For <span className="font-semibold">{dcUploadModal.order_id}</span> -- enter the customer's own DC details and upload the document.
          </p>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">DC Number *</label>
          <input
            value={dcNumber}
            onChange={(e) => setDcNumber(e.target.value)}
            placeholder="e.g. DC-2026-4471"
            data-testid="dc-number-input"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm mb-3 focus:outline-none focus:border-teal-400"
          />
          <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">Declared Value (₹) *</label>
          <input
            type="number"
            value={dcValue}
            onChange={(e) => setDcValue(e.target.value)}
            placeholder="e.g. 25000"
            data-testid="dc-value-input"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm mb-3 focus:outline-none focus:border-teal-400"
          />
          <label className="block text-[11px] font-semibold text-slate-600 uppercase mb-1">DC File (PDF or Image) *</label>
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => setDcFile(e.target.files?.[0] || null)}
            data-testid="dc-file-input"
            className="w-full border border-slate-300 rounded-lg p-2 text-sm mb-1"
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => { setDcUploadModal(null); setDcNumber(""); setDcValue(""); setDcFile(null); }}
              className="flex-1 px-4 py-2 text-slate-500 text-sm font-semibold hover:text-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={submitDCUpload}
              disabled={!dcNumber.trim() || !dcValue || !dcFile || dcSubmitting}
              data-testid="confirm-dc-upload-btn"
              className="flex-1 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              {dcSubmitting ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    )}
    </DashboardLayout>
  );
}
